import { ReactNode } from 'react';

import { format, getMonth, getYear, startOfDay } from 'date-fns';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { dateFnsLocales } from '@utils/date';
import { transitionFast } from '@utils/transitions';

import { GRID_COLUMNS, useGridArrowNav } from './arrow-nav';
import { getYearPeriod, YEAR_GRID_SIZE } from './helpers';
import { DAY_CELL_PX, disabledCellStyle, interactiveCellStyle, selectedCellStyle } from './styles';
import { LanguageOptions } from './types';

// Group a flat cell list into rows of GRID_COLUMNS for the grid/row/gridcell
// ARIA structure below.
const toRows = <T,>(cells: T[]): T[][] => {
  const rows: T[][] = [];
  for (let i = 0; i < cells.length; i += GRID_COLUMNS) {
    rows.push(cells.slice(i, i + GRID_COLUMNS));
  }
  return rows;
};

// Shared ARIA shell for the month/year grids, mirroring what react-day-picker
// gives the day grid: a named grid of rows whose gridcells carry
// aria-selected, with the interactive button inside the cell. Without this a
// screen reader hears twelve bare "June, button" announcements with no
// structure and no way to tell which one is selected.
const GridShell = (props: {
  label: string;
  cells: { key: string; selected: boolean; button: ReactNode }[];
}) => (
  <Grid role="grid" aria-label={props.label}>
    {toRows(props.cells).map((row) => (
      <GridRow role="row" key={row[0].key}>
        {row.map((cell) => (
          <GridCellShell role="gridcell" aria-selected={cell.selected || undefined} key={cell.key}>
            {cell.button}
          </GridCellShell>
        ))}
      </GridRow>
    ))}
  </Grid>
);

type MonthGridProps = {
  year: number;
  selected: Date | undefined;
  lang: LanguageOptions;
  minDate?: Date;
  maxDate?: Date;
  onSelect: (date: Date) => void;
};

export const MonthGrid = (props: MonthGridProps) => {
  const { year, selected, lang, minDate, maxDate, onSelect } = props;
  const months = Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));
  // The solid highlight (data-selected): the committed selection, else today
  // (same fallback as the day view), shown only in its own year. Other years
  // get no marking at all — mirroring the selection's month into them proved
  // confusing (see the day grid, which dropped its equivalent preview).
  const highlight = selected ?? new Date();
  const highlightYear = getYear(highlight);
  const highlightMonth = getMonth(highlight);

  // A month is in range when any of its days are, so test its last day against
  // minDate and its first day against maxDate — both at day granularity (like
  // the day grid's matchers) so a minDate/maxDate carrying a wall-clock time
  // can't wrongly disable a month whose boundary day is still selectable.
  const isOutOfRange = (monthDate: Date) => {
    const monthEnd = new Date(year, getMonth(monthDate) + 1, 0);
    if (minDate && startOfDay(monthEnd) < startOfDay(minDate)) return true;
    if (maxDate && startOfDay(monthDate) > startOfDay(maxDate)) return true;
    return false;
  };

  const disabled = months.map(isOutOfRange);
  const getCellProps = useGridArrowNav(disabled);

  return (
    <GridShell
      label={String(year)}
      cells={months.map((monthDate, index) => {
        const isSelected = highlightMonth === getMonth(monthDate) && highlightYear === year;
        return {
          key: String(monthDate.getMonth()),
          selected: isSelected,
          button: (
            <GridCell
              {...getCellProps(index)}
              type="button"
              data-testid="datepicker-month"
              onClick={() => onSelect(monthDate)}
              data-selected={isSelected || undefined}
              disabled={disabled[index]}
            >
              {format(monthDate, 'MMMM', { locale: dateFnsLocales[lang] })}
            </GridCell>
          ),
        };
      })}
    />
  );
};

type YearGridProps = {
  anchorYear: number;
  selected: Date | undefined;
  minDate?: Date;
  maxDate?: Date;
  onSelect: (date: Date) => void;
};

export const YearGrid = (props: YearGridProps) => {
  const { anchorYear, selected, minDate, maxDate, onSelect } = props;
  const [startPeriod, endPeriod] = getYearPeriod(anchorYear);
  const years = Array.from({ length: YEAR_GRID_SIZE }, (_, i) => startPeriod + i);
  // Fall back to today's year when nothing is selected — matches the day and
  // month views.
  const highlightYear = getYear(selected ?? new Date());

  const isOutOfRange = (year: number) => {
    if (minDate && year < getYear(minDate)) return true;
    if (maxDate && year > getYear(maxDate)) return true;
    return false;
  };

  const disabled = years.map(isOutOfRange);
  const getCellProps = useGridArrowNav(disabled);

  return (
    <GridShell
      label={`${startPeriod} - ${endPeriod}`}
      cells={years.map((year, index) => {
        const isHighlighted = highlightYear === year;
        return {
          key: String(year),
          selected: isHighlighted,
          button: (
            <GridCell
              {...getCellProps(index)}
              type="button"
              data-testid="datepicker-year"
              onClick={() => onSelect(new Date(year, 0, 1))}
              data-selected={isHighlighted || undefined}
              disabled={disabled[index]}
            >
              {year}
            </GridCell>
          ),
        };
      })}
    />
  );
};

const Grid = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const GridRow = styled.div({
  display: 'grid',
  gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
});

const GridCellShell = styled.div({
  display: 'flex',
});

const GridCell = styled.button({
  border: 'none',
  background: 'transparent',
  width: '100%',
  height: pxToRem(DAY_CELL_PX),
  font: theme.font.regular14,
  color: theme.color.textPrimary,
  borderRadius: pxToRem(8),
  cursor: 'pointer',
  outline: 'none',
  transition: transitionFast('background-color'),
  ...interactiveCellStyle,
  '&[data-selected]': selectedCellStyle,
  ':disabled': disabledCellStyle,
});
